import { Component, ElementRef } from '@angular/core';

import * as d3 from 'd3';

import { Serie } from '../../../models/serie.type';
import { TooltipData } from '../../../models/tooltip-data.type';
import { BaseAxisChartComponent } from '../base/base-axis-chart.component';
import { AccessorType } from '../../../models';
import { ChartingService } from '../../../services';

@Component({
    selector: 'aa-timeline-chart',
    templateUrl: './timeline-chart.component.html',
    styleUrls: [
        '../../../container.scss',
        './timeline-chart.component.scss'
    ],
    providers: [
        ChartingService
    ],
    standalone: false
})
export class TimelineChartComponent extends BaseAxisChartComponent
{
    public xScale: d3.ScaleTime<number, number>;
    public yScale: d3.ScaleLinear<number, number>;
    public y0AccessorScaled: AccessorType;
    public keyAccessor: AccessorType = (i) => i;
    public visibleSeries: Serie[];

    constructor (
        elementRef: ElementRef,
        public chartingService: ChartingService
    )
    {
        super(elementRef);
    }

    protected onInit ()
    {
        this.chartingService.config = this.cfg;
        this.xFormatTick = this.chartingService.xTimeFormatTick;
        this.yFormatTick = this.chartingService.yFormatTick;
        if (!this.chartingService.config.title.draw)
        {
            this.chartingService.config.dimensions.marginTop /= 2;
        }

        this.dimensions = {
            ...this.dimensions,
            ...this.chartingService.config.dimensions
        };

        this.calcBoundedDims();
    }

    onMousePosition ($event: any)
    {
        const { x, y } = this.container.nativeElement.getBoundingClientRect();
        const hoveredX: any = this.xScale.invert($event[0] - x - this.dimensions.marginLeft);
        const hoveredY: any = this.yScale.invert($event[1] - y - this.dimensions.marginTop);

        const getDistanceFromHoveredX = (d: any) =>
        {
            return Math.abs(this.series[0].xAccessor(d) - hoveredX);
        };
        const closestXIndex = d3.leastIndex(this.series[0].dataPoints, (a, b) => (
            getDistanceFromHoveredX(a) - getDistanceFromHoveredX(b)
        ));
        const closestDataPoint = this.series[0].dataPoints[closestXIndex];
        const closestXValue = this.series[0].xAccessor(closestDataPoint);

        const closestYValues: number[] = [];
        let closestYIndex: number = -1;
        let minimum: number = Infinity;
        this.series.forEach((serie: Serie, idx: number) =>
        {
            const cdp = serie.dataPoints[closestXIndex];
            const dist = Math.abs(serie.yAccessor(cdp) - hoveredY);
            if (dist < minimum)
            {
                minimum = dist;
                closestYIndex = idx;
            }
            closestYValues.push(serie.yAccessor(cdp));
        });

        const closestYValue = closestYValues[closestYIndex];

        this.tooltipData = new TooltipData(this.xFormatTick(closestXValue));
        this.tooltipData.posX = this.xScale(closestXValue);
        this.tooltipData.posY = this.yScale(closestYValue);
        this.tooltipData.unit = this.series[0].unit;
        this.visibleSeries
            .forEach((serie, idx) =>
            {
                if (closestYValues[idx])
                {
                    this.tooltipData.values
                        .push({
                            label: serie.label,
                            color: serie.config.color,
                            value: closestYValues[idx]
                        });
                }
            });
    }

    public adjustScales (): void
    {
        this.series.forEach((elt: Serie, idx: number) =>
        {
            elt.id = idx;
            elt.dataPoints = this.data; // .map((d: any) => [Number(elt.xRawAccessor(d)), Number(elt.yAccessor(d))]);
        });
        this.visibleSeries = this.series.filter((s: Serie) => s.isVisible);

        this.mins = [];
        this.maxs = [];
        if (this.visibleSeries.length > 0)
        {
            if (this.chartingService.config?.type === 'percentage')
            {
                this.mins = [0];
                this.maxs = [100];
            }
            else
            {
                this.visibleSeries.forEach((serie: Serie) =>
                {
                    this.mins.push(d3.min(this.data, serie.yAccessor));
                    this.maxs.push(d3.max(this.data, serie.yAccessor));
                });
            }

            this.xScale = d3.scaleTime()
                .domain(d3.extent(this.data, this.visibleSeries[0].xAccessor))
                .range([0, this.dimensions.boundedWidth]);
            // .nice();
            this.yScale = d3.scaleLinear()
                .domain([d3.min(this.mins), d3.max(this.maxs)])
                .range([this.dimensions.boundedHeight, 0])
                .nice();

            this.visibleSeries.forEach((serie: Serie) =>
            {
                serie.y0AccessorScaled = () =>
                    this.yScale(this.yScale.domain()[0]);
                serie.xAccessorScaled = (d: any) =>
                    this.xScale(serie.xAccessor(d));
                serie.yAccessorScaled = (d: any) =>
                    this.yScale(serie.yAccessor(d));
            });
        }
    }

    public onToggleSerieVisibility ()
    {
        this.adjustScales();
    }
}
